import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
    CURRENT_CLINIC_CUSTOMER_ID_QUERY_KEY,
    CURRENT_CLINIC_ID_QUERY_KEY,
} from "@/lib/react-query/queryKeys";

export interface ClinicSummary {
    id: string;
    name: string;
    customer_id: string;
    customer_name: string;
}

export function useClinicsForUser() {
    const { user } = useAuth();

    return useQuery<ClinicSummary[]>({
        queryKey: ["clinics-for-user", user?.id ?? null],
        queryFn: async () => {
            const userId = user?.id;
            if (!userId) return [];

            const { data, error } = await supabase
                .from("user_clinic_access")
                .select(
                    `clinic:clinics(id, name, customer_id, customer:customers(name))`
                )
                .eq("user_id", userId);

            if (error) throw error;
            type Row = {
                clinic: {
                    id: string;
                    name: string;
                    customer_id: string;
                    customer: { name: string } | null;
                } | null
            };
            const clinics = ((data || []) as Row[])
                .map((row) => row.clinic)
                .filter(Boolean) as {
                    id: string;
                    name: string;
                    customer_id: string;
                    customer: { name: string } | null;
                }[];
            return clinics.map((c) => ({
                id: c.id,
                name: c.name,
                customer_id: c.customer_id,
                customer_name: c.customer?.name || "Sem organização"
            }));
        },
        enabled: !!user,
        staleTime: 30_000,
    });
}

export function useSwitchClinic() {
    const queryClient = useQueryClient();
    const { user } = useAuth();
    return useMutation({
        mutationFn: async (clinicId: string) => {
            const userId = user?.id;
            if (!userId) throw new Error("Usuário não autenticado");

            const { error } = await supabase
                .from("profiles")
                .update({ current_clinic_id: clinicId })
                .eq("id", userId);

            if (error) throw error;
        },
        onMutate: async (clinicId: string) => {
            await queryClient.cancelQueries({ queryKey: CURRENT_CLINIC_ID_QUERY_KEY });
            const previous = queryClient.getQueryData(CURRENT_CLINIC_ID_QUERY_KEY);
            queryClient.setQueryData(CURRENT_CLINIC_ID_QUERY_KEY, clinicId);
            return { previous };
        },
        onError: (_err, _vars, context) => {
            if (context?.previous !== undefined) {
                queryClient.setQueryData(CURRENT_CLINIC_ID_QUERY_KEY, context.previous);
            }
        },
        onSuccess: async () => {
            queryClient.removeQueries({ type: "inactive" });
            await queryClient.resetQueries({ type: "active" });
        },
    });
}

export function useClinicsByCustomer(customerId: string | null) {
    return useQuery({
        queryKey: ["clinics-by-customer", customerId],
        queryFn: async () => {
            if (!customerId) return [];

            const { data, error } = await supabase
                .from("clinics")
                .select("id, name")
                .eq("customer_id", customerId)
                .order("name");

            if (error) throw error;
            return data;
        },
        enabled: !!customerId,
        staleTime: 300000, // 5 minutos
    });
}
